import { useEffect, useState } from 'react';
import { ChevronDown, ChevronRight, MapPin, Plus, Trash2, Truck } from 'lucide-react';
import {
  createDeliveryAddress,
  deleteDeliveryAddress,
  fetchClientAddress,
  fetchDeliveryAddresses,
  updateDeliveryAddress,
  updatePackage,
} from '@/lib/api';
import type { DeliveryAddress, GiftPackage } from '@/lib/types';

interface Props {
  pkg: GiftPackage;
  onChanged: () => void;
}

const inputCls =
  'w-full rounded border border-bmade-200 bg-white px-2 py-1 text-xs text-bmade-950 outline-none focus:border-accent-500 dark:border-bmade-700 dark:bg-bmade-950/60 dark:text-white';

const labelCls = 'mb-0.5 block text-[10px] uppercase tracking-wider text-bmade-500';

export function DeliveryPanel({ pkg, onChanged }: Props) {
  const [open, setOpen] = useState(false);
  const [addresses, setAddresses] = useState<DeliveryAddress[]>([]);
  const [loading, setLoading] = useState(false);
  const [adding, setAdding] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [clientHasAddress, setClientHasAddress] = useState(false);

  useEffect(() => {
    if (!pkg.client_id) {
      setClientHasAddress(false);
      return;
    }
    fetchClientAddress(pkg.client_id)
      .then((a) => setClientHasAddress(Boolean(a?.street && a?.city)))
      .catch(() => setClientHasAddress(false));
  }, [pkg.client_id]);

  // Handling-velden lokaal voor debounced save
  const [party, setParty] = useState(pkg.handling_party ?? '');
  const [partyName, setPartyName] = useState(pkg.handling_party_name ?? '');
  const [date, setDate] = useState(pkg.handling_date ?? '');
  const [notes, setNotes] = useState(pkg.delivery_notes ?? '');

  useEffect(() => {
    setParty(pkg.handling_party ?? '');
    setPartyName(pkg.handling_party_name ?? '');
    setDate(pkg.handling_date ?? '');
    setNotes(pkg.delivery_notes ?? '');
  }, [pkg.id]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const changed =
      party !== (pkg.handling_party ?? '') ||
      partyName !== (pkg.handling_party_name ?? '') ||
      date !== (pkg.handling_date ?? '') ||
      notes !== (pkg.delivery_notes ?? '');
    if (!changed) return;
    const t = setTimeout(() => {
      updatePackage(pkg.id, {
        handling_party: (party || null) as GiftPackage['handling_party'],
        handling_party_name: partyName || null,
        handling_date: date || null,
        delivery_notes: notes || null,
      }).then(() => onChanged());
    }, 600);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [party, partyName, date, notes]);

  async function loadAddresses() {
    setLoading(true);
    try {
      setAddresses(await fetchDeliveryAddresses(pkg.id));
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    if (open) void loadAddresses();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, pkg.id]);

  async function addAddress(useClientDefault: boolean) {
    setAdding(true);
    setError(null);
    try {
      let prefill = {
        street: '',
        postal_code: '',
        city: '',
        country: 'NL',
        contact_name: null as string | null,
        contact_phone: null as string | null,
      };
      if (useClientDefault && pkg.client_id) {
        const c = await fetchClientAddress(pkg.client_id);
        if (c) {
          prefill = {
            street: c.street ?? '',
            postal_code: c.postal_code ?? '',
            city: c.city ?? '',
            country: c.country ?? 'NL',
            contact_name: c.contact_name,
            contact_phone: c.contact_phone,
          };
        }
      }
      await createDeliveryAddress({
        package_id: pkg.id,
        label: useClientDefault ? 'Klant-hoofdadres' : null,
        ...prefill,
        packages_count: pkg.quantity - addresses.reduce((s, a) => s + a.packages_count, 0) || 1,
        delivery_window_start: null,
        delivery_window_end: null,
        notes: null,
      });
      await loadAddresses();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Adres toevoegen mislukt');
    } finally {
      setAdding(false);
    }
  }

  async function patchAddress(id: string, patch: Partial<DeliveryAddress>) {
    await updateDeliveryAddress(id, patch);
    setAddresses((prev) => prev.map((a) => (a.id === id ? { ...a, ...patch } : a)));
  }

  async function removeAddress(id: string) {
    await deleteDeliveryAddress(id);
    setAddresses((prev) => prev.filter((a) => a.id !== id));
  }

  const totalAssigned = addresses.reduce((s, a) => s + a.packages_count, 0);
  const valid = totalAssigned === pkg.quantity;

  return (
    <div className="rounded-xl border border-bmade-200 bg-white dark:border-bmade-800 dark:bg-bmade-900/40">
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className="flex w-full items-center justify-between px-4 py-3 text-xs font-semibold uppercase tracking-wider text-bmade-700 dark:text-bmade-300"
      >
        <span className="inline-flex items-center gap-2">
          <Truck className="h-3 w-3" />
          Uitlevering
        </span>
        {open ? <ChevronDown className="h-3 w-3" /> : <ChevronRight className="h-3 w-3" />}
      </button>

      {open && (
        <div className="space-y-4 border-t border-bmade-200 px-4 py-3 dark:border-bmade-800">
          <fieldset className="space-y-2 rounded-md border border-bmade-200 p-3 dark:border-bmade-800">
            <legend className="px-1 text-[10px] uppercase tracking-wider text-bmade-500">
              Handling
            </legend>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className={labelCls}>Wie handelt?</label>
                <select
                  value={party}
                  onChange={(e) => setParty(e.target.value)}
                  className={inputCls}
                >
                  <option value="">— kies —</option>
                  <option value="2bmade">2BMade</option>
                  <option value="2bmade_rotary">2BMade + Rotary</option>
                  <option value="extern">Extern</option>
                </select>
              </div>
              <div>
                <label className={labelCls}>Handlings-datum</label>
                <input
                  type="date"
                  value={date}
                  onChange={(e) => setDate(e.target.value)}
                  className={inputCls}
                />
              </div>
            </div>
            {party === 'extern' && (
              <div>
                <label className={labelCls}>Naam externe partij</label>
                <input
                  type="text"
                  value={partyName}
                  onChange={(e) => setPartyName(e.target.value)}
                  className={inputCls}
                />
              </div>
            )}
            <div>
              <label className={labelCls}>Klantwensen (verzending)</label>
              <textarea
                rows={2}
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="bv. mag niet voor 14:00 leveren, achter het magazijn afzetten"
                className={inputCls}
              />
            </div>
          </fieldset>

          <fieldset className="space-y-2 rounded-md border border-bmade-200 p-3 dark:border-bmade-800">
            <legend className="px-1 text-[10px] uppercase tracking-wider text-bmade-500">
              Afleveradressen
            </legend>

            {loading ? (
              <p className="text-xs text-bmade-500">Laden...</p>
            ) : addresses.length === 0 ? (
              <p className="text-xs text-bmade-500">
                Nog geen afleveradressen. Voeg er één toe of meerdere als pakketten over locaties
                verdeeld worden.
              </p>
            ) : (
              <ul className="space-y-2">
                {addresses.map((a) => (
                  <AddressRow
                    key={a.id}
                    address={a}
                    onChange={(patch) => patchAddress(a.id, patch)}
                    onRemove={() => removeAddress(a.id)}
                  />
                ))}
              </ul>
            )}

            {error && (
              <div className="rounded border border-red-300 bg-red-50 px-2 py-1 text-[11px] text-red-700 dark:border-red-500/40 dark:bg-red-500/10 dark:text-red-300">
                {error}
              </div>
            )}

            <div className="flex items-center justify-between border-t border-bmade-200 pt-2 text-xs dark:border-bmade-800">
              <span
                className={
                  valid
                    ? 'text-emerald-600 dark:text-emerald-400'
                    : 'text-amber-600 dark:text-amber-400'
                }
              >
                <MapPin className="mr-1 inline h-3 w-3" />
                {totalAssigned} / {pkg.quantity} pakketten toegewezen
              </span>
              <div className="flex items-center gap-1">
                {clientHasAddress && (
                  <button
                    type="button"
                    disabled={adding}
                    onClick={() => addAddress(true)}
                    className="inline-flex items-center gap-1 rounded border border-bmade-200 px-2 py-1 text-bmade-700 hover:border-accent-400 hover:text-accent-700 disabled:opacity-40 dark:border-bmade-700 dark:text-bmade-300 dark:hover:border-accent-400 dark:hover:text-accent-300"
                    title="Voeg klant-hoofdadres toe"
                  >
                    <Plus className="h-3 w-3" /> Klant-adres
                  </button>
                )}
                <button
                  type="button"
                  disabled={adding}
                  onClick={() => addAddress(false)}
                  className="inline-flex items-center gap-1 rounded border border-bmade-200 px-2 py-1 text-bmade-700 hover:border-accent-400 hover:text-accent-700 disabled:opacity-40 dark:border-bmade-700 dark:text-bmade-300 dark:hover:border-accent-400 dark:hover:text-accent-300"
                >
                  <Plus className="h-3 w-3" /> {adding ? 'Bezig...' : 'Leeg adres'}
                </button>
              </div>
            </div>
          </fieldset>
        </div>
      )}
    </div>
  );
}

function AddressRow({
  address,
  onChange,
  onRemove,
}: {
  address: DeliveryAddress;
  onChange: (patch: Partial<DeliveryAddress>) => void | Promise<void>;
  onRemove: () => void;
}) {
  const [local, setLocal] = useState(address);
  useEffect(() => setLocal(address), [address.id]); // eslint-disable-line react-hooks/exhaustive-deps

  function field<K extends keyof DeliveryAddress>(key: K, value: DeliveryAddress[K]) {
    setLocal((prev) => ({ ...prev, [key]: value }));
  }
  function blur(key: keyof DeliveryAddress) {
    if (local[key] !== address[key]) onChange({ [key]: local[key] } as Partial<DeliveryAddress>);
  }

  return (
    <li className="space-y-1 rounded border border-bmade-200 bg-bmade-50/60 p-2 dark:border-bmade-800 dark:bg-bmade-950/40">
      <div className="grid grid-cols-12 gap-1.5">
        <input
          className={`${inputCls} col-span-5`}
          value={local.street ?? ''}
          onChange={(e) => field('street', e.target.value)}
          onBlur={() => blur('street')}
          placeholder="Straat + nr"
        />
        <input
          className={`${inputCls} col-span-2`}
          value={local.postal_code ?? ''}
          onChange={(e) => field('postal_code', e.target.value)}
          onBlur={() => blur('postal_code')}
          placeholder="PC"
        />
        <input
          className={`${inputCls} col-span-3`}
          value={local.city ?? ''}
          onChange={(e) => field('city', e.target.value)}
          onBlur={() => blur('city')}
          placeholder="Plaats"
        />
        <input
          type="number"
          min={1}
          className={`${inputCls} col-span-2 text-center`}
          value={local.packages_count}
          onChange={(e) => field('packages_count', parseInt(e.target.value) || 1)}
          onBlur={() => blur('packages_count')}
          placeholder="#"
        />
      </div>
      <div className="grid grid-cols-12 gap-1.5">
        <input
          className={`${inputCls} col-span-4`}
          value={local.contact_name ?? ''}
          onChange={(e) => field('contact_name', e.target.value)}
          onBlur={() => blur('contact_name')}
          placeholder="Contact-naam"
        />
        <input
          className={`${inputCls} col-span-3`}
          value={local.contact_phone ?? ''}
          onChange={(e) => field('contact_phone', e.target.value)}
          onBlur={() => blur('contact_phone')}
          placeholder="Telefoon"
        />
        <input
          className={`${inputCls} col-span-4`}
          value={local.notes ?? ''}
          onChange={(e) => field('notes', e.target.value)}
          onBlur={() => blur('notes')}
          placeholder="Notitie"
        />
        <button
          type="button"
          onClick={onRemove}
          title="Adres verwijderen"
          className="col-span-1 rounded border border-bmade-200 text-bmade-500 hover:border-red-400 hover:bg-red-50 hover:text-red-600 dark:border-bmade-700 dark:hover:bg-red-500/10"
        >
          <Trash2 className="mx-auto h-3 w-3" />
        </button>
      </div>
    </li>
  );
}
